import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Plus, Trash2, Edit3, Save, X, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const blank = { nom: "", siren: "" };

export default function SocietesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // id being edited
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () =>
    api.get("/societes").then((r) => setItems(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blank); setShowForm(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ nom: s.nom, siren: s.siren || "" }); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(blank); };

  const save = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est obligatoire."); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/societes/${editing}`, form);
        toast.success("Société modifiée.");
      } else {
        await api.post("/societes", form);
        toast.success("Société ajoutée.");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette société ?")) return;
    setDeletingId(id);
    try {
      await api.delete(`/societes/${id}`);
      toast.success("Société supprimée.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          {items.length} société(s)
        </div>
        <button
          data-testid="add-societe-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 text-sm font-medium px-3 py-1.5 transition-colors"
        >
          <Plus size={13} strokeWidth={2} /> Ajouter
        </button>
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="border border-zinc-300 bg-zinc-50 p-4 mb-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
            {editing ? "Modifier la société" : "Nouvelle société"}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
              Nom <span className="text-[#DC2626]">*</span>
            </label>
            <input
              data-testid="societe-nom-input"
              autoFocus
              type="text"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && save()}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-zinc-600"
              placeholder="Ex : Cabinet Dupont"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
              SIREN <span className="text-zinc-400 normal-case">(optionnel)</span>
            </label>
            <input
              data-testid="societe-siren-input"
              type="text"
              value={form.siren}
              onChange={(e) => setForm({ ...form, siren: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && save()}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-zinc-600"
              placeholder="123456789"
              maxLength={9}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              data-testid="societe-save-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-[#1A3A6E] hover:bg-[#112952] text-white text-sm px-4 py-2 transition-colors disabled:opacity-40"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button
              onClick={cancel}
              className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-100 transition-colors"
            >
              <X size={13} />
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-sm text-zinc-400 font-mono py-8 text-center">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-zinc-200 py-12 text-center">
          <Building2 size={28} strokeWidth={1} className="text-zinc-300 mx-auto mb-3" />
          <div className="text-sm text-zinc-400">Aucune société. Ajoutez-en une.</div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`societe-row-${s.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
            >
              <Building2 size={14} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate">{s.nom}</div>
                {s.siren && (
                  <div className="text-[10px] font-mono text-zinc-400">SIREN : {s.siren}</div>
                )}
              </div>
              <button
                data-testid={`societe-edit-${s.id}`}
                onClick={() => openEdit(s)}
                className="text-zinc-400 hover:text-zinc-700 transition-colors p-1"
                title="Modifier"
              >
                <Edit3 size={13} strokeWidth={1.5} />
              </button>
              <button
                data-testid={`societe-delete-${s.id}`}
                onClick={() => remove(s.id)}
                disabled={deletingId === s.id}
                className="text-zinc-400 hover:text-[#DC2626] transition-colors p-1 disabled:opacity-40"
                title="Supprimer"
              >
                {deletingId === s.id
                  ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" />
                  : <Trash2 size={13} strokeWidth={1.5} />
                }
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
